import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Input,
  Button,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Table from 'components/Table';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import {
  deleteTipoDeAlerta,
  getTiposDeAlertas,
  patchTipoDeAlerta,
  postTipoDeAlerta,
} from 'api/configuracion/tiposDeAlertas';
import { getCatalogoDePadrones } from 'api/catalogos';
import { getConfiguracionesDeSegmentosDeFolios } from 'api/configuracion/configuracionesDeFolios';
import { capitalize } from 'utils/normalizers';
import PlainTransfer from 'components/PlainTransfer';
import usePermissionMessage from 'hooks/usePermissionMessage';
import { getSistemas } from 'api/recaudacion/sistemas';
import { getTiposDeCargos } from 'api/recaudacion/tiposDeCargo';

const { TextArea } = Input;

export const permissionConfig = {
  model: 'tipodealerta',
  catalogs: [
    'padron',
    'configuraciondesegmentofolio',
    'origendelaalerta',
    'sistema',
  ],
};

// eslint-disable-next-line react/prop-types
const TiposDeAlertas = ({ permission }) => {
  const padrones = useSelector(({ catalogs }) => catalogs.padrones);
  const [form] = Form.useForm();
  const es_informativa = Form.useWatch('es_informativa', form);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [folios, setFolios] = useState([]);
  const [sistemas, setSistemas] = useState([]);
  const [tiposDeCargos, setTiposDeCargos] = useState({ results: [] });

  usePermissionMessage(permission);

  const fetchTiposDeCargos = async (page = 1, descripcion = undefined) => {
    const padron = form.getFieldValue('padron');
    if (!padron || form.getFieldValue('es_informativa')) return;
    const _tiposDeCargos = await getTiposDeCargos({ page, padron, descripcion });
    setTiposDeCargos(_tiposDeCargos);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeAlertas();
    setData(_data);
    setLoading(false);
    setTimeout(() => fetchTiposDeCargos(), 1000);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await getCatalogoDePadrones();
      const dataFolios = await getConfiguracionesDeSegmentosDeFolios();
      const _sistemas = await getSistemas();

      if (mounted) {
        setFolios(dataFolios);
        setSistemas(_sistemas);
      }

      await fetchData();
      setLoading(false);
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values.estados_globales = 1;
      if (es_informativa) {
        values.sistemas = [];
        values.cargos = [];
      }
      const [key] = selectedRowKeys;
      const saved = await (!key ? postTipoDeAlerta(values)
        : patchTipoDeAlerta(key, values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteTipoDeAlerta(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Padrón',
      dataIndex: 'padron',
      key: 'padron',
      width: 300,
      render: (val) => (padrones.find((item) => item.id === val)?.descripcion),
    },
    {
      titleText: 'Tipo de Alerta',
      dataIndex: 'es_informativa',
      key: 'es_informativa',
      width: 300,
      render: (val) => (val ? 'Informativa' : 'Restrictiva'),
    },
  ];

  const required = { required: true };

  const rules = {
    required: [required],
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickDelete,
              onClickEdit,
            }}
            rowKey="id"
            mobileColIndex={0}
            permission={permission}
          />
        ) : (
          <Card
            className="form-container"
            title={`${!selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Alerta`}
            extra={(
              <FormSubmitControls
                onFinish={() => onFinish()}
                onCancel={onCancel}
                loading={loading}
                permission={permission}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={() => onFinish()}
              scrollToFirstError
              initialValues={{
                estados_globales: 1,
                es_informativa: true,
                permite_cobrar_cargos: true,
              }}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    name="descripcion"
                    rules={rules.required}
                    label="Descripción"
                  >
                    <TextArea autoSize={{ minRows: 4, maxRows: 5 }} allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                  >
                    <TextArea autoSize={{ minRows: 4, maxRows: 5 }} allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="es_informativa"
                    rules={rules.required}
                    label="Tipo de alerta"
                  >
                    <Select trueFalse={['Informativa', 'Restrictiva']} onChange={() => fetchTiposDeCargos()} />
                  </Form.Item>
                </Col>
                {/* <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="permite_cobrar_cargos"
                    rules={rules.required}
                    label="Permite cobrar cargos"
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col> */}
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="origen_de_alerta"
                    rules={rules.required}
                    label="Origen de Alerta"
                    normalize={capitalize}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="padron"
                    rules={rules.required}
                    label="Padrón"
                  >
                    <Select onChange={() => fetchTiposDeCargos()} dataSource={padrones} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="folio"
                    rules={rules.required}
                    label="Foliador"
                  >
                    <Select dataSource={folios} />
                  </Form.Item>
                </Col>
                {!es_informativa && (
                  <>
                    <PlainTransfer
                      formItemName="sistemas"
                      dataSource={sistemas}
                      label="Sistemas cobrables"
                    />
                    <PlainTransfer
                      formItemName="cargos"
                      dataSource={tiposDeCargos}
                      callbackPagination={fetchTiposDeCargos}
                      label="Tipos de cargos cobrables"
                      pagination={false}
                    />
                  </>
                )}
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Row>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`Tipo de Alerta ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TiposDeAlertas;
